import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReportDraftPreview } from '../components/reports/Preview/ReportDraftPreview';
import { getReportById } from '../utils/mockReportsData';
import { buildReportTemplateDocument } from '../utils/reportTemplateGenerator';
import { getDossierByReportId } from '../utils/mockDossierData';
import { MOCK_STATIONS } from '../utils/mockStationsData';
import {
  getDraftStorageKey,
  getSerializedEditorState,
  isLegacyDraftPayload,
} from '../utils/reportDraftStorage';
import { loadBestDraft } from '../utils/reportDraftRepository';
import { sanitizeSerializedStateForOfficialExport } from '../utils/reportExportSanitizer';
import { buildPdfChromeMetadata } from '../utils/reportPdfChromeMetadata';
import { resolveLanguageCode } from '../utils/locale';
import { STORAGE_PREFIXES, getPrimaryStorageKey, readStorageValue } from '../utils/storageKeys';
import './ReportPrintPage.css';

export const getReportExportStorageKey = (reportId) => (
  reportId ? getPrimaryStorageKey(STORAGE_PREFIXES.reportExport, reportId) : null
);
const OFFICIAL_EXPORT_PROFILE = 'official_v1';

const loadExportPayload = (reportId) => {
  if (!reportId || typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = readStorageValue(window.localStorage, STORAGE_PREFIXES.reportExport, { suffix: reportId });
    if (!raw?.value) {
      return null;
    }
    const parsed = JSON.parse(raw.value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

const withTimeout = (promise, timeoutMs) => Promise.race([
  promise,
  new Promise((resolve) => setTimeout(resolve, timeoutMs)),
]);

const waitForImage = (image) => {
  if (!image) {
    return Promise.resolve();
  }
  if (image.complete) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = () => {
      image.removeEventListener('load', done);
      image.removeEventListener('error', done);
      resolve();
    };
    image.addEventListener('load', done);
    image.addEventListener('error', done);
  });
};

const getFirstCoverBlock = (templateDocument) => (
  (templateDocument?.sections || [])
    .flatMap(section => section?.blocks || [])
    .find(block => block?.type === 'cover') || null
);

const getTocHeadingTitle = (templateDocument, locale) => {
  const tocSection = (templateDocument?.sections || []).find(section => section?.id === 'toc');
  if (tocSection?.title) {
    return tocSection.title;
  }
  if (locale === 'es') {
    return 'Índice';
  }
  if (locale === 'en') {
    return 'Contents';
  }
  return 'Indice';
};

export function ReportPrintPage() {
  const { id, lang } = useParams();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const [exportPayload, setExportPayload] = useState(() => loadExportPayload(id));
  const isPreviewMode = searchParams.get('mode') === 'preview';
  const exportProfile = isPreviewMode ? OFFICIAL_EXPORT_PROFILE : (exportPayload?.exportProfile || null);
  const isOfficialExport = exportProfile === OFFICIAL_EXPORT_PROFILE;
  const locale = useMemo(() => {
    if (typeof lang === 'string' && lang.trim().length > 0) {
      return resolveLanguageCode(lang);
    }
    return resolveLanguageCode(i18n.language);
  }, [lang, i18n.language]);
  const [storedDraft, setStoredDraft] = useState(null);
  const [isDraftLoadResolved, setIsDraftLoadResolved] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setExportPayload(loadExportPayload(id));
  }, [id]);

  const report = useMemo(() => {
    if (exportPayload?.reportPayload) {
      return exportPayload.reportPayload;
    }
    return getReportById(id) || null;
  }, [id, exportPayload]);

  const reportId = report?.id || id;
  const storageKey = useMemo(() => getDraftStorageKey(reportId, locale), [reportId, locale]);

  useEffect(() => {
    let isCancelled = false;
    setIsDraftLoadResolved(false);
    (async () => {
      let nextDraft = null;
      try {
        nextDraft = await loadBestDraft({ reportId, locale, storageKey });
      } catch (error) {
        nextDraft = null;
      }
      if (isCancelled) {
        return;
      }
      setStoredDraft(nextDraft);
      setIsDraftLoadResolved(true);
    })();
    return () => {
      isCancelled = true;
    };
  }, [locale, reportId, storageKey]);

  const serializedState = useMemo(() => {
    if (typeof exportPayload?.serializedState === 'string' && exportPayload.serializedState) {
      return exportPayload.serializedState;
    }
    if (!isDraftLoadResolved) {
      return null;
    }
    return getSerializedEditorState(storedDraft);
  }, [exportPayload, isDraftLoadResolved, storedDraft]);

  const dossier = useMemo(() => {
    if (!report) {
      return null;
    }
    return getDossierByReportId(report.id, report.type);
  }, [report]);

  const templateT = useMemo(() => i18n.getFixedT(locale), [i18n, locale]);

  const templateDocument = useMemo(() => {
    if (exportPayload?.templateDocument?.sections?.length) {
      return exportPayload.templateDocument;
    }
    if (!report) {
      return null;
    }
    const payload = buildReportTemplateDocument({
      report,
      reportType: report?.type,
      locale,
      t: templateT,
      dossier,
      stations: MOCK_STATIONS,
    });
    return payload?.document || null;
  }, [exportPayload, report, locale, templateT, dossier]);
  const printTemplateDocument = useMemo(() => {
    if (!templateDocument) {
      return null;
    }
    if (!isOfficialExport) {
      return templateDocument;
    }
    const exportSections = Array.isArray(templateDocument.exportSections)
      && templateDocument.exportSections.length > 0
      ? templateDocument.exportSections
      : templateDocument.sections;
    return {
      ...templateDocument,
      locale,
      sections: exportSections,
    };
  }, [templateDocument, isOfficialExport, locale]);
  const templateCoverBlock = useMemo(
    () => getFirstCoverBlock(printTemplateDocument),
    [printTemplateDocument]
  );
  const tocHeadingTitle = useMemo(
    () => getTocHeadingTitle(printTemplateDocument, locale),
    [printTemplateDocument, locale]
  );
  const shouldSanitizeDraft = useMemo(
    () => isOfficialExport || isLegacyDraftPayload(storedDraft),
    [isOfficialExport, storedDraft]
  );
  const sanitizedSerializedState = useMemo(() => {
    if (!serializedState || !shouldSanitizeDraft) {
      return serializedState;
    }
    return sanitizeSerializedStateForOfficialExport(serializedState, locale, {
      maxTocLevel: isOfficialExport ? 2 : null,
      removeInternalSections: isOfficialExport,
      normalizeStructure: isOfficialExport,
      tocHeadingTitle,
      templateCoverBlock,
      templateDocument: printTemplateDocument,
    });
  }, [
    serializedState,
    shouldSanitizeDraft,
    locale,
    isOfficialExport,
    tocHeadingTitle,
    templateCoverBlock,
    printTemplateDocument,
  ]);
  const pdfChromeMetadata = useMemo(
    () => buildPdfChromeMetadata({
      reportPayload: report,
      reportId,
      templateDocument: printTemplateDocument,
      locale,
    }),
    [report, reportId, printTemplateDocument, locale]
  );

  useEffect(() => {
    let cancelled = false;
    setIsReady(false);

    const prepare = async () => {
      try {
        if (document?.fonts?.ready) {
          await withTimeout(document.fonts.ready, 2500);
        }
      } catch (error) {
        // Continue readiness checks even if font loading fails.
      }

      const images = Array.from(document.querySelectorAll('img'));
      await withTimeout(Promise.allSettled(images.map(waitForImage)), 3500);

      if (!cancelled) {
        setTimeout(() => {
          if (!cancelled) {
            setIsReady(true);
          }
        }, 250);
      }
    };

    prepare();

    return () => {
      cancelled = true;
    };
  }, [printTemplateDocument, sanitizedSerializedState]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const previousLang = document.documentElement.getAttribute('lang');
    document.documentElement.setAttribute('lang', locale);
    document.body.dataset.reportExportProfile = exportProfile || 'default';
    document.body.dataset.reportLocale = locale;
    document.body.dataset.reportPaginationProfile = 'print';
    return () => {
      if (previousLang) {
        document.documentElement.setAttribute('lang', previousLang);
      } else {
        document.documentElement.removeAttribute('lang');
      }
      delete document.body.dataset.reportExportProfile;
      delete document.body.dataset.reportLocale;
      delete document.body.dataset.reportPaginationProfile;
    };
  }, [exportProfile, locale]);

  return (
    <main
      className="report-print-page"
      data-report-print-ready={isReady ? 'true' : 'false'}
      data-report-export-profile={exportProfile || 'default'}
      data-report-locale={locale}
      data-report-view-mode={isPreviewMode ? 'preview' : 'export'}
    >
      <ReportDraftPreview
        serializedState={sanitizedSerializedState}
        templateDocument={printTemplateDocument}
        exportProfile={exportProfile}
        previewMode={isPreviewMode ? 'pdf_exact' : 'default'}
        pdfChromeMetadata={isPreviewMode ? pdfChromeMetadata : null}
        paginationMode="strict"
        paginationProfile="print"
      />
    </main>
  );
}

export default ReportPrintPage;
